'use client';

import React from 'react'
import { User, Settings, LogOut, Shield, PenLine, UserCheck, X } from 'lucide-react'
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button'
import { iconMap } from '@sourcepilot/ui/framework/ui/atoms/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@sourcepilot/ui/framework/ui/atoms/dropdown-menu'
import { Logo } from '@sourcepilot/ui/framework/ui/atoms/logo'
import { UserAvatar } from '@sourcepilot/ui/framework/ui/atoms/user-avatar'
import { Switch } from '@sourcepilot/ui/framework/ui/atoms/switch'
import { useSimpleAuth as useAuth } from '@sourcepilot/ui/shell/auth/use-simple-auth';
import { useAdminMode } from '@sourcepilot/ui/shell/auth/use-admin-mode';
import { useImpersonation } from '@sourcepilot/ui/shell/auth/use-impersonation';
import { useRouter } from 'next/navigation';
const get_org_icon = () => {
  const Icn: React.ComponentType<any> | undefined = iconMap['organisation'];
  if (!Icn) return null;
  return <Icn className="h-3 w-3 flex-shrink-0 text-gray-800" />;
}



export function TopBar() {
  const [hasMounted, setHasMounted] = React.useState(false);
  React.useEffect(() => { setHasMounted(true); }, []);

  const { logout, user } = useAuth();
  const router = useRouter();
  // /auth/user carries the caller's User NODE id — no query needed, and
  // correct under admin mode (a list query's first() would be arbitrary)
  const goProfile = () =>
    router.push(user?.user_node_id ? `/users/${user.user_node_id}` : '/users');
  const { adminMode, adminWriteMode, isAdmin, setAdminMode, setAdminWriteMode } = useAdminMode();
  const { isImpersonating, impersonatedPerson, canWrite, stopImpersonation, setWriteMode } = useImpersonation();

  return (
    <div className="h-[45px] flex items-center px-4 w-full pt-1 pb-1 bg-gray-800">
      <div className="flex-shrink-0">
        <Logo />
      </div>

      {/* Impersonation banner - shown when impersonating (takes precedence over admin mode) */}
      {hasMounted && isImpersonating && (
        <div className="ml-4 flex items-center gap-2 px-3 py-1 bg-orange-500/20 border border-orange-500/50 rounded-md">
          <UserCheck className="h-4 w-4 text-orange-400" />
          <span className="text-xs font-medium text-orange-400">
            VIEWING AS: {impersonatedPerson?.name || 'a user'}
          </span>
          <button
            onClick={() => stopImpersonation()}
            className="ml-2 p-0.5 hover:bg-orange-500/30 rounded"
            title="Exit impersonation"
          >
            <X className="h-3 w-3 text-orange-400" />
          </button>
        </div>
      )}

      {/* Impersonation write mode indicator */}
      {hasMounted && isImpersonating && canWrite && (
        <div className="ml-2 flex items-center gap-2 px-3 py-1 bg-red-500/20 border border-red-500/50 rounded-md">
          <PenLine className="h-4 w-4 text-red-400" />
          <span className="text-xs font-medium text-red-400">WRITE ENABLED</span>
        </div>
      )}

      {/* Admin mode indicator - shown when active (not during impersonation) */}
      {hasMounted && adminMode && !isImpersonating && (
        <div className="ml-4 flex items-center gap-2 px-3 py-1 bg-amber-500/20 border border-amber-500/50 rounded-md">
          <Shield className="h-4 w-4 text-amber-400" />
          <span className="text-xs font-medium text-amber-400">ADMIN MODE</span>
        </div>
      )}

      {/* Admin write mode indicator - shown when active (not during impersonation) */}
      {hasMounted && adminWriteMode && !isImpersonating && (
        <div className="ml-2 flex items-center gap-2 px-3 py-1 bg-red-500/20 border border-red-500/50 rounded-md">
          <PenLine className="h-4 w-4 text-red-400" />
          <span className="text-xs font-medium text-red-400">WRITE ENABLED</span>
        </div>
      )}

      <div className="flex-1"></div>

      {/* Right-side buttons */}
      <div className="flex items-center space-x-2">
        {/* Impersonation write mode toggle (only when impersonating) */}
        {hasMounted && isImpersonating && (
          <div className="flex items-center gap-2 mr-2">
            <label
              htmlFor="impersonate-write-mode"
              className="text-xs text-orange-400 cursor-pointer"
            >
              Write
            </label>
            <Switch
              id="impersonate-write-mode"
              checked={canWrite}
              onCheckedChange={setWriteMode}
            />
          </div>
        )}

        {/* Admin mode toggle (only for admins, hidden during impersonation) */}
        {hasMounted && isAdmin && !isImpersonating && (
          <div className="flex items-center gap-2 mr-2">
            <label
              htmlFor="admin-mode"
              className="text-xs text-gray-400 cursor-pointer"
            >
              Admin
            </label>
            <Switch
              id="admin-mode"
              checked={adminMode}
              onCheckedChange={setAdminMode}
            />
          </div>
        )}

        {/* Admin write mode toggle (only when admin mode is on, hidden during impersonation) */}
        {hasMounted && adminMode && !isImpersonating && (
          <div className="flex items-center gap-2 mr-2">
            <label
              htmlFor="admin-write-mode"
              className="text-xs text-red-400 cursor-pointer"
            >
              Write
            </label>
            <Switch
              id="admin-write-mode"
              checked={adminWriteMode}
              onCheckedChange={setAdminWriteMode}
            />
          </div>
        )}

        {/* Org menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              {get_org_icon()}
              <span className="sr-only text-white">Organisation settings</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">

          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="user-menu">
              <UserAvatar  />
              <span className="sr-only">User settings</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={goProfile}>
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={goProfile}>
              {/* the profile page carries the editable user settings; a
                  dedicated settings page can take this slot later */}
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={logout} data-testid="user-menu-logout">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

